import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

export function stripIsoFormatting(value: string): string {
  return value
    .replace('T', ' ')
    .replace(/\.\d+/, '')
    .replace(/(Z|[+-]\d{2}:?\d{2})$/, '');
}

const HAS_OFFSET = /(Z|[+-]\d{2}:?\d{2})$/;

// Resolve an API timestamp to a true UTC instant. We auto-detect the convention
// from the string itself:
//   - If it has a trailing offset (e.g. "+00:00" or "Z"), trust it as UTC.
//   - If it has no offset (e.g. "2026-06-12T07:27:15.394138"), treat the
//     wall-clock as store-local.
//  Falls back to plain Date parsing if no timezone is known.
export function parseInstant(value: string, timeZone?: string): Date | null {
  if (HAS_OFFSET.test(value)) {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  if (timeZone) {
    const d = dayjs.tz(value, timeZone);
    return d.isValid() ? d.toDate() : null;
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

function getOffsetLabel(instant: Date, timeZone?: string): string {
  if (isNaN(instant.getTime())) return 'UTC';
  const parts = new Intl.DateTimeFormat('en-CA', {
    ...(timeZone ? { timeZone } : {}),
    timeZoneName: 'shortOffset',
  }).formatToParts(instant);
  const raw = parts.find((p) => p.type === 'timeZoneName')?.value;
  return raw ? raw.replace('GMT', 'UTC') : 'UTC';
}

// Format an instant using the user's default locale, in the given timezone
// (or the browser's default zone if omitted), appending "(UTC±N)".
export function formatInstantInTimeZone(instant: Date, timeZone?: string): string {
  if (isNaN(instant.getTime())) return '';
  const formatted = new Intl.DateTimeFormat(undefined, {
    ...(timeZone ? { timeZone } : {}),
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  }).format(instant);
  return `${formatted} (${getOffsetLabel(instant, timeZone)})`;
}

export function formatInStoreTimeZone(value: string, timeZone: string | undefined): string {
  if (!value) return '';
  if (!timeZone) return stripIsoFormatting(value);
  const instant = parseInstant(value, timeZone);
  if (!instant) return stripIsoFormatting(value);
  return formatInstantInTimeZone(instant, timeZone);
}
